/** Formateadores y etiquetas de presentación de Tarifas (US-2.4, es-CO). */
import type { Tariff, TariffComponentType, TariffType } from '@goelectric/shared-types';

/** Etiqueta visible de cada tipo de tarifa (tuteo, sin jerga innecesaria). */
export const TARIFF_TYPE_LABELS: Record<TariffType, string> = {
  FLAT: 'Plana',
  TOU: 'TOU (pico-valle)',
  IDLE_FEE: 'Idle Fee',
};

/** Descripción breve del tipo para tooltips/ayudas. */
export const TARIFF_TYPE_DESCRIPTIONS: Record<TariffType, string> = {
  FLAT: 'Un solo precio por kWh o cargo fijo.',
  TOU: 'Precio variable por franja horaria (pico/valle).',
  IDLE_FEE: 'Cargo por minuto cuando el vehículo sigue conectado tras cargar.',
};

/** Etiqueta visible de cada componente de costo. */
export const TARIFF_COMPONENT_LABELS: Record<TariffComponentType, string> = {
  ENERGY: 'Energía',
  SESSION: 'Sesión',
  TIME: 'Tiempo',
  IDLE_FEE: 'Idle Fee',
  FLAT: 'Cargo fijo',
};

/** Sufijo de unidad del precio según el componente. */
const COMPONENT_UNIT_SUFFIX: Record<TariffComponentType, string> = {
  ENERGY: ' / kWh',
  SESSION: ' / sesión',
  TIME: ' / min',
  IDLE_FEE: ' / min',
  FLAT: ' fijo',
};

/** Formatea un monto en pesos colombianos (`$ 1.150`). */
export function formatCop(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '—';
  }
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 2,
  }).format(value);
}

/** Precio con la unidad del componente (`$ 850 / kWh`). */
export function formatComponentPrice(
  priceCop: number | null | undefined,
  type: TariffComponentType,
): string {
  return `${formatCop(priceCop)}${COMPONENT_UNIT_SUFFIX[type]}`;
}

/** Fecha/hora corta local (es-CO); `—` si falta o es inválida. */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return '—';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Vigencia legible: `desde → hasta` o "Sin fecha de fin". */
export function formatVigencia(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
): string {
  const from = formatDateTime(validFrom);
  const to = validTo ? formatDateTime(validTo) : 'Sin fecha de fin';
  return `${from} → ${to}`;
}

/**
 * Precio representativo para la tabla: en TOU resume el rango por kWh; en el resto
 * usa el primer componente y avisa cuántos más hay.
 */
export function representativePrice(tariff: Tariff): string {
  const periods = tariff.touPeriods ?? [];
  if (tariff.type === 'TOU' && periods.length > 0) {
    const prices = periods.map((period) => period.priceCopPerKwh).filter((price) => !Number.isNaN(price));
    if (prices.length > 0) {
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      const range = min === max ? formatCop(min) : `${formatCop(min)} – ${formatCop(max)}`;
      return `${range} / kWh`;
    }
  }

  const components = tariff.components ?? [];
  const first = components[0];
  if (!first) {
    return '—';
  }
  const base = formatComponentPrice(first.priceCop, first.type);
  return components.length > 1 ? `${base} · ${components.length} componentes` : base;
}
